import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';
import { getBundleStore } from '@theme/pdp-bundle-store';
import { formatMoney } from '@theme/money-formatting';

const POPIN_PREFIX = 'bbv2-';
const DIRECT_ADD_SOURCE = 'buy-box-v2';
const ADDED_STATE_DURATION = 2000;
const STICKY_FOOTER_MARGIN = '0px 0px 200px 0px';
const STICKY_LOADING_TIMEOUT = 8000;
const REASSURANCE_RESUME_DELAY = 2000;
/** Server-rendered Yotpo reviews app block, then the widget Yotpo injects. */
const REVIEWS_SELECTOR = '[id*="__yotpo_product_reviews_reviews"], #yotpo-reviews-main-widget';

/**
 * Buy Box V2 root: opens the matching `<dialog-component>` for any `#bbv2-*` link or button
 * (rich text links included), resolves `#<section_key>` anchors to the rendered
 * `shopify-section-…__<section_key>` wrapper, handles the cross-sell "direct add" buttons and
 * shows the sticky add-to-cart once the main button has scrolled past.
 *
 * @extends {Component}
 */
class BuyBoxV2 extends Component {
  /** @type {Set<number>} */
  #resetTimeouts = new Set();

  /** @type {AbortController | null} */
  #stickyController = null;

  /** @type {IntersectionObserver[]} */
  #stickyObservers = [];

  #ctaHidden = false;

  #footerNear = false;

  /** @type {number | undefined} */
  #stickyLoadingTimeout;

  /** @type {MutationObserver | null} */
  #truemedObserver = null;

  /** @type {AbortController | null} */
  #reassuranceController = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerover', this.#onModelOver);
    this.addEventListener('pointerout', this.#onModelOut);
    this.#setupSticky();
    this.#setupTruemed();
    this.#setupReassurance();
    document.addEventListener('model:update', this.#onModelUpdate);
    document.addEventListener('pdp:bundle:change', this.#onBundleChange);
    this.#renderRecap(this.#bundleSnapshot());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerover', this.#onModelOver);
    this.removeEventListener('pointerout', this.#onModelOut);
    document.removeEventListener('model:update', this.#onModelUpdate);
    document.removeEventListener('pdp:bundle:change', this.#onBundleChange);
    this.#teardownSticky();
    this.#reassuranceController?.abort();
    this.#reassuranceController = null;
    this.#truemedObserver?.disconnect();
    this.#truemedObserver = null;

    for (const timeoutId of this.#resetTimeouts) {
      clearTimeout(timeoutId);
    }
    this.#resetTimeouts.clear();
  }

  /** Rebuilds the sticky observers after a model switch: the morph replaces the observed button. */
  #onModelUpdate = () => {
    this.#teardownSticky();
    this.#setupSticky();
    this.#setupReassurance();
    this.#renderRecap(this.#bundleSnapshot());
  };

  /** Loops every reassurance row (skipped with reduced motion, the row is then a plain scroller). */
  #setupReassurance() {
    this.#reassuranceController?.abort();
    this.#reassuranceController = null;
    if (prefersReducedMotion()) return;

    const controller = new AbortController();
    this.#reassuranceController = controller;
    for (const row of this.querySelectorAll('.buy-box-v2__reassurance')) {
      if (row instanceof HTMLElement) this.#loopReassurance(row, controller.signal);
    }
  }

  /**
   * Scrolls a reassurance row as a seamless loop (its list is rendered twice) that can still be
   * swiped or scrolled by hand: a touch, a wheel or a manual scroll pauses it for a moment, hovering
   * with a mouse pauses it until the pointer leaves. Runs only while the row is on screen.
   * @param {HTMLElement} row
   * @param {AbortSignal} signal
   */
  #loopReassurance(row, signal) {
    const list = row.querySelector('.buy-box-v2__reassurance-list');
    const track = list?.parentElement;
    if (!(list instanceof HTMLElement) || !(track instanceof HTMLElement)) return;

    const duration = parseFloat(getComputedStyle(row).getPropertyValue('--bbv2-loop-duration')) || 20;
    let loopWidth = 0;
    let position = row.scrollLeft;
    let resumeAt = 0;
    let touching = false;
    let hovered = false;
    let frame = 0;
    let last = 0;

    const hold = () => {
      resumeAt = performance.now() + REASSURANCE_RESUME_DELAY;
    };

    /** @param {number} time */
    const tick = (time) => {
      const delta = last ? Math.min(time - last, 64) : 0;
      last = time;
      if (Math.abs(row.scrollLeft - position) > 2) position = row.scrollLeft;

      const playing = !touching && !hovered && time >= resumeAt;
      if (playing) position += (loopWidth / duration) * (delta / 1000);
      const wrap = !touching && loopWidth > 0 && position >= loopWidth;
      if (wrap) position -= loopWidth;
      if (playing || wrap) row.scrollLeft = position;

      frame = requestAnimationFrame(tick);
    };

    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      last = 0;
    };

    const resize = new ResizeObserver(() => {
      loopWidth = list.offsetWidth + (parseFloat(getComputedStyle(track).columnGap) || 0);
    });
    resize.observe(list);

    const visibility = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting && !frame) frame = requestAnimationFrame(tick);
      else if (!entry?.isIntersecting) stop();
    });
    visibility.observe(row);

    const options = { signal, passive: true };
    row.addEventListener('touchstart', () => (touching = true), options);
    row.addEventListener(
      'touchend',
      () => {
        touching = false;
        hold();
      },
      options
    );
    row.addEventListener('touchcancel', () => (touching = false), options);
    row.addEventListener('wheel', hold, options);
    row.addEventListener(
      'scroll',
      () => {
        if (Math.abs(row.scrollLeft - position) > 2) hold();
      },
      options
    );
    row.addEventListener(
      'pointerenter',
      (event) => {
        if (event.pointerType === 'mouse') hovered = true;
      },
      options
    );
    row.addEventListener('pointerleave', () => (hovered = false), options);
    signal.addEventListener('abort', () => {
      stop();
      resize.disconnect();
      visibility.disconnect();
    });
  }

  /** @param {Event} event */
  #onBundleChange = (event) => {
    const sectionId = this.dataset.sectionId ?? '';
    const detail = event instanceof CustomEvent ? event.detail : null;
    if (sectionId && detail?.sectionId !== sectionId) return;
    this.#renderRecap(this.#bundleSnapshot());
  };

  /**
   * Bundle snapshot of this buy box's section (product-information can share the page).
   * @returns {{ contributors: Array<{ display: Array<{ role: string, subtitle?: string }> }>, totalCents: number }}
   */
  #bundleSnapshot() {
    return getBundleStore().snapshot(this.dataset.sectionId ?? '');
  }

  /**
   * Fills the buy card recap (extras + total) and the sticky prices from the bundle snapshot.
   * @param {{ contributors: Array<{ display: Array<{ role: string, subtitle?: string }> }>, totalCents: number }} snapshot
   */
  #renderRecap(snapshot) {
    const line = this.querySelector('[data-bbv2-recap]');
    if (!(line instanceof HTMLElement)) return;

    const display = snapshot.contributors.flatMap((contributor) => contributor.display);
    /** @param {string} role */
    const count = (role) => display.filter((item) => item.role === role).length;
    /**
     * @param {number} value
     * @param {string} key
     */
    const plural = (value, key) =>
      (line.dataset[value === 1 ? `${key}One` : `${key}Other`] ?? '').replace('[count]', String(value));

    /** @type {string[]} */
    const parts = [];
    const bands = count('band');
    if (bands > 0) parts.push(plural(bands, 'bands'));
    const accessories = count('xsell');
    if (accessories > 0) parts.push(plural(accessories, 'accessories'));
    const plan = display.find((item) => item.role === 'subscription');
    if (plan?.subtitle) parts.push((line.dataset.plan ?? '[plan]').replace('[plan]', plan.subtitle));

    const extras = line.querySelector('[data-bbv2-recap-extras]');
    if (extras) extras.textContent = parts.join(' · ');
    const extrasWrapper = extras?.parentElement;
    if (extrasWrapper) extrasWrapper.dataset.empty = String(parts.length === 0);

    const hasTotal = snapshot.totalCents > 0;
    const totalCents = Number(line.dataset.priceCents ?? 0) + snapshot.totalCents;
    const formatted = formatMoney(totalCents, line.dataset.moneyFormat ?? '{{amount}}', line.dataset.currency ?? 'USD');
    line.toggleAttribute('data-has-total', hasTotal);

    const total = line.querySelector('[data-bbv2-recap-total]');
    if (total instanceof HTMLElement && total.textContent !== formatted) {
      const changed = total.textContent !== '';
      total.textContent = formatted;
      if (changed && hasTotal) total.dataset.tick = total.dataset.tick === 'a' ? 'b' : 'a';
    }

    const stickyTotal = formatMoney(
      totalCents,
      line.dataset.stickyMoneyFormat ?? line.dataset.moneyFormat ?? '{{amount}}',
      line.dataset.currency ?? 'USD'
    );
    for (const price of this.querySelectorAll('.buy-box-v2-sticky__price, .buy-box-v2-sticky__button-price')) {
      if (!(price instanceof HTMLElement)) continue;
      price.dataset.basePrice ??= price.textContent ?? '';
      price.textContent = hasTotal ? stickyTotal : price.dataset.basePrice;
    }
  }

  /**
   * Shows the hovered model's color and size in the model heading (mouse only).
   * @param {PointerEvent} event
   */
  #onModelOver = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
    const tile = event.target.closest('a.buy-box-v2-models__tile');
    if (!(tile instanceof HTMLElement)) return;
    this.#setModelHeading(tile, tile.dataset.color, tile.dataset.size, true);
  };

  /**
   * Restores the current model in the heading, unless the hovered tile was just picked.
   * @param {PointerEvent} event
   */
  #onModelOut = (event) => {
    if (!(event.target instanceof Element)) return;
    const tile = event.target.closest('a.buy-box-v2-models__tile');
    if (!(tile instanceof HTMLElement) || tile.hasAttribute('data-picked')) return;
    if (event.relatedTarget instanceof Node && tile.contains(event.relatedTarget)) return;
    const heading = tile.closest('.buy-box-v2-models')?.querySelector('[data-models-heading]');
    if (!(heading instanceof HTMLElement)) return;
    this.#setModelHeading(tile, heading.dataset.color, heading.dataset.size, false);
  };

  /**
   * @param {Element} tile - A tile of the model picker to update
   * @param {string | undefined} color
   * @param {string | undefined} size
   * @param {boolean} preview - Whether the heading shows another model than the current one
   */
  #setModelHeading(tile, color, size, preview) {
    const heading = tile.closest('.buy-box-v2-models')?.querySelector('[data-models-heading]');
    if (!(heading instanceof HTMLElement)) return;
    const colorText = heading.querySelector('[data-models-color]');
    const sizeText = heading.querySelector('[data-models-size]');
    if (colorText && color) colorText.textContent = color;
    if (sizeText && size) sizeText.textContent = size;
    heading.toggleAttribute('data-preview', preview);
  }

  /**
   * Moves the selection ring to the clicked model right away, while model-picker fetches it.
   * @param {MouseEvent} event
   */
  #pickModel(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    const tile = event.target.closest('a.buy-box-v2-models__tile');
    if (!(tile instanceof HTMLElement)) return;
    tile.closest('.buy-box-v2-models')?.querySelector('[data-picked]')?.removeAttribute('data-picked');
    tile.setAttribute('data-picked', '');
    this.#setModelHeading(tile, tile.dataset.color, tile.dataset.size, false);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    this.#pickModel(event);

    const stickyButton = event.target.closest('button[data-bbv2-sticky-atc]');
    if (stickyButton instanceof HTMLButtonElement && this.contains(stickyButton)) {
      this.#relayAddToCart(stickyButton);
      return;
    }

    const addButton = event.target.closest('button[data-bbv2-add-variant]');
    if (addButton instanceof HTMLButtonElement && this.contains(addButton)) {
      event.preventDefault();
      this.#directAdd(addButton);
      return;
    }

    if (this.#scrollToSectionKey(event)) return;

    const trigger = event.target.closest('a[href*="#bbv2-"], button[data-popin-target]');
    if (!trigger || !this.contains(trigger)) return;

    const target = trigger.getAttribute('data-popin-target') ?? trigger.getAttribute('href') ?? '';
    const popinId = target.slice(target.indexOf('#') + 1);
    if (!popinId.startsWith(POPIN_PREFIX)) return;

    /** @type {(HTMLElement & { showDialog?: () => void }) | null} */
    const popin = document.getElementById(popinId);
    if (!popin || !this.contains(popin) || typeof popin.showDialog !== 'function') return;

    event.preventDefault();
    popin.showDialog();
  };

  /**
   * Reveals the Truemed "Learn more" buttons once the Truemed widget has rendered in its hidden source,
   * which means the product is eligible and the widget has bound its modal to `.truemed-instructions-open`.
   */
  #setupTruemed() {
    const source = this.querySelector('[data-bbv2-truemed-source]');
    const triggers = this.querySelectorAll('[data-bbv2-truemed-trigger]');
    if (!source || triggers.length === 0) return;

    const reveal = () => {
      if (!source.querySelector('.truemed-entry-box')) return false;
      for (const trigger of triggers) {
        if (trigger instanceof HTMLElement) trigger.hidden = false;
      }
      return true;
    };

    if (reveal()) return;

    this.#truemedObserver = new MutationObserver(() => {
      if (!reveal()) return;
      this.#truemedObserver?.disconnect();
      this.#truemedObserver = null;
    });
    this.#truemedObserver.observe(source, { childList: true, subtree: true });
  }

  /** @returns {HTMLElement | null} */
  get #sticky() {
    return this.querySelector('[data-bbv2-sticky]');
  }

  /** @returns {HTMLButtonElement | null} */
  get #mainAddToCartButton() {
    const button = this.querySelector('.buy-box-v2__atc');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /** Watches the main add-to-cart button and the footer to toggle the sticky add-to-cart. */
  #setupSticky() {
    const sticky = this.#sticky;
    const target = this.#mainAddToCartButton?.closest('.buy-box-v2__buttons') ?? this.#mainAddToCartButton;
    if (!sticky || !target) return;

    this.#stickyController = new AbortController();
    const { signal } = this.#stickyController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onStickyCartSettled, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#onStickyCartError, { signal });

    const ctaObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.#ctaHidden = !entry.isIntersecting;
      this.#syncSticky();
    });
    ctaObserver.observe(target);
    this.#stickyObservers.push(ctaObserver);

    const footer =
      document.querySelector('[class*="shopify-section-group-footer-group"]') ?? document.querySelector('footer');
    if (!footer) return;

    const footerObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#footerNear = entry.isIntersecting;
        this.#syncSticky();
      },
      { rootMargin: STICKY_FOOTER_MARGIN }
    );
    footerObserver.observe(footer);
    this.#stickyObservers.push(footerObserver);
  }

  #teardownSticky() {
    clearTimeout(this.#stickyLoadingTimeout);
    this.#stickyController?.abort();
    this.#stickyController = null;
    for (const observer of this.#stickyObservers) {
      observer.disconnect();
    }
    this.#stickyObservers = [];
    this.#ctaHidden = false;
    this.#footerNear = false;
  }

  /** Shown whenever the main button is out of view (above or below), except near the footer. */
  #syncSticky() {
    const sticky = this.#sticky;
    if (!sticky) return;

    const visible = this.#ctaHidden && !this.#footerNear;
    sticky.dataset.visible = String(visible);
    sticky.toggleAttribute('inert', !visible);
  }

  /**
   * Relays the sticky click to the main add-to-cart button, so the bundle store and
   * pre-order properties are submitted exactly as from the buy box.
   * @param {HTMLButtonElement} button
   */
  #relayAddToCart(button) {
    const target = this.#mainAddToCartButton;
    if (!target || target.disabled || button.dataset.state === 'loading') return;

    button.dataset.state = 'loading';
    button.setAttribute('aria-busy', 'true');
    target.dataset.puppet = 'true';
    target.click();

    clearTimeout(this.#stickyLoadingTimeout);
    this.#stickyLoadingTimeout = window.setTimeout(() => this.#resetStickyButton(), STICKY_LOADING_TIMEOUT);
  }

  #resetStickyButton() {
    clearTimeout(this.#stickyLoadingTimeout);
    const button = this.querySelector('button[data-bbv2-sticky-atc]');
    const target = this.#mainAddToCartButton;
    if (target) target.dataset.puppet = 'false';
    if (!(button instanceof HTMLButtonElement) || button.dataset.state !== 'loading') return false;

    button.dataset.state = 'idle';
    button.removeAttribute('aria-busy');
    return true;
  }

  #onStickyCartSettled = () => {
    this.#resetStickyButton();
  };

  #onStickyCartError = () => {
    if (!this.#resetStickyButton()) return;

    const error = this.querySelector('.product-form-text__error:not(.hidden)');
    error?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'center',
    });
  };

  /**
   * Adds one cross-sell variant to the cart right away and opens the cart drawer.
   * @param {HTMLButtonElement} button
   * @returns {Promise<void>}
   */
  async #directAdd(button) {
    if (button.disabled || button.dataset.state === 'loading') return;

    const variantId = Number(button.dataset.bbv2AddVariant);
    if (!variantId) return;

    button.dataset.state = 'loading';
    button.setAttribute('aria-busy', 'true');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
        })
      );
      const data = await response.json();

      if (data.status) {
        document.dispatchEvent(new CartErrorEvent(DIRECT_ADD_SOURCE, data.message, data.description, data.errors));
        button.dataset.state = 'idle';
        this.#announce(button, data.description ?? data.message ?? '');
        return;
      }

      const cart = (await this.#fetchCart()) ?? data;
      document.dispatchEvent(
        new CartAddEvent(cart, DIRECT_ADD_SOURCE, {
          source: DIRECT_ADD_SOURCE,
          productId: this.dataset.productId,
          itemCount: 1,
          variantId: String(variantId),
          sections: data.sections,
        })
      );

      button.dataset.state = 'added';
      this.#announce(button, button.dataset.addedMessage ?? '');
      this.#openCartDrawer();

      const timeoutId = window.setTimeout(() => {
        button.dataset.state = 'idle';
        this.#resetTimeouts.delete(timeoutId);
      }, ADDED_STATE_DURATION);
      this.#resetTimeouts.add(timeoutId);
    } catch (error) {
      console.error('Buy Box V2 direct add error:', error);
      document.dispatchEvent(new CartErrorEvent(DIRECT_ADD_SOURCE, 'Network error', '', {}));
      button.dataset.state = 'idle';
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /** @returns {Promise<Record<string, unknown> | null>} Full cart, for listeners expecting `item_count` */
  async #fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return response.ok ? await response.json() : null;
    } catch {
      return null;
    }
  }

  /** Opens the cart drawer, honouring its `auto-open` setting (mirrors cart-drawer.js). */
  #openCartDrawer() {
    requestAnimationFrame(() => {
      /** @type {(HTMLElement & { open?: () => void, refs?: { dialog?: HTMLDialogElement } }) | null} */
      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer || !drawer.hasAttribute('auto-open') || drawer.refs?.dialog?.open) return;
      if (typeof drawer.open === 'function') drawer.open();
    });
  }

  /**
   * @param {HTMLElement} origin
   * @param {string} message
   */
  #announce(origin, message) {
    const status = origin.closest('.buy-box-v2-xsell')?.querySelector('[data-bbv2-add-status]');
    if (!status || !message) return;
    status.textContent = message;
  }

  /**
   * @param {MouseEvent} event
   * @returns {boolean} Whether the click was handled
   */
  #scrollToSectionKey(event) {
    if (!(event.target instanceof Element)) return false;

    const link = event.target.closest('a[href^="#"]');
    if (!link || !this.contains(link)) return false;

    const hash = decodeURIComponent(link.getAttribute('href')?.slice(1) ?? '');
    if (!hash || hash.startsWith(POPIN_PREFIX)) return false;

    const reviewsLink = link.hasAttribute('data-bbv2-reviews-link');
    if (!reviewsLink && document.getElementById(hash)) return false;

    const target =
      (reviewsLink ? document.getElementById(hash) : null) ??
      document.querySelector(`[id$="__${CSS.escape(hash)}"]`) ??
      (reviewsLink ? document.querySelector(REVIEWS_SELECTOR)?.closest('.shopify-section') : null);
    if (!target) return false;

    event.preventDefault();
    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    return true;
  }
}

if (!customElements.get('buy-box-v2')) {
  customElements.define('buy-box-v2', BuyBoxV2);
}
